import { Anchor, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { DataTable, type DataTableColumn, type DataTableSortStatus } from 'mantine-datatable';
import { Link } from 'react-router-dom';
import { StatusBadge } from './status-badge';
import { getIntegrationMeta } from './integration-meta';
import { PAGE_SIZE } from '../magic-values';
import type { IntegrationInstallation } from '../types/integration-installation';

interface IntegrationInstallationsTableProps {
  installations: IntegrationInstallation[];
  fetching?: boolean;
  onRowClick?: (installation: IntegrationInstallation) => void;
  page: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  sortStatus: DataTableSortStatus;
  onSortStatusChange: (status: DataTableSortStatus) => void;
  height?: string | number;
}

function formatDate(value: string | undefined): string {
  return value ? new Date(value).toLocaleString() : '—';
}

const COLUMNS: DataTableColumn<IntegrationInstallation>[] = [
  {
    accessor: 'key',
    title: 'Integration',
    sortable: true,
    render: (installation) => {
      const meta = getIntegrationMeta(installation.key);
      const Icon = meta.icon;
      return (
        <Group spacing="sm" noWrap>
          <ThemeIcon size={28} radius="md" variant="light" color="bagel">
            <Icon size={16} />
          </ThemeIcon>
          <Stack spacing={0}>
            <Text size="sm">{meta.label}</Text>
            <Text size="xs" color="dimmed" sx={{ fontFamily: 'monospace' }}>
              {installation._id}
            </Text>
          </Stack>
        </Group>
      );
    },
  },
  {
    accessor: 'status',
    title: 'Status',
    sortable: true,
    render: (installation) => <StatusBadge status={installation.status} />,
  },
  {
    accessor: 'authType',
    title: 'Auth Type',
    sortable: true,
    render: (installation) => installation.authType,
  },
  {
    accessor: 'connectingUserId',
    title: 'Connected By',
    render: (installation) =>
      installation.connectingUserId ? (
        <Anchor
          component={Link}
          to={`/user/${installation.connectingUserId}`}
          size="xs"
          sx={{ fontFamily: 'monospace' }}
          onClick={(event) => event.stopPropagation()}
        >
          {installation.connectingUserId}
        </Anchor>
      ) : (
        '—'
      ),
  },
  {
    accessor: 'createdAt',
    title: 'Created',
    sortable: true,
    render: (installation) => formatDate(installation.createdAt),
  },
];

export function IntegrationInstallationsTable({
  installations,
  fetching,
  onRowClick,
  page,
  totalRecords,
  onPageChange,
  sortStatus,
  onSortStatusChange,
  height = 'calc(100vh - 500px)',
}: IntegrationInstallationsTableProps) {
  return (
    <DataTable
      striped
      highlightOnHover
      height={height}
      sx={{ minWidth: 900 }}
      records={installations}
      idAccessor="_id"
      fetching={fetching}
      noRecordsText="No integrations installed."
      columns={COLUMNS}
      onRowClick={onRowClick ? (installation) => onRowClick(installation) : undefined}
      totalRecords={totalRecords}
      recordsPerPage={PAGE_SIZE}
      page={page}
      onPageChange={onPageChange}
      sortStatus={sortStatus}
      onSortStatusChange={onSortStatusChange}
    />
  );
}
